import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';

export type OverlayState = 'idle' | 'loading' | 'success' | 'error';

@Component({
  selector: 'app-action-overlay',
  imports: [ButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (state() !== 'idle') {
      <div
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        [attr.aria-label]="ariaLabel()">
        <div
          class="mx-4 w-full max-w-md rounded-xl border p-6 shadow-2xl bg-white border-gray-200 dark:bg-zinc-900 dark:border-zinc-700">

          @switch (state()) {
            @case ('loading') {
              <div class="flex flex-col items-center gap-4 text-center">
                <i class="pi pi-spin pi-spinner text-4xl text-emerald-500"></i>
                <p class="text-lg font-medium text-gray-800 dark:text-neutral-200">
                  {{ loadingLabel() }}
                </p>
                <p class="text-sm text-gray-500 dark:text-zinc-400">
                  {{ loadingSublabel() }}
                </p>
              </div>
            }
            @case ('success') {
              <div class="flex flex-col items-center gap-4 text-center">
                <i class="pi pi-check-circle text-4xl text-emerald-500"></i>
                <p class="text-lg font-medium text-gray-800 dark:text-neutral-200">
                  {{ successLabel() }}
                </p>
              </div>
            }
            @case ('error') {
              <div class="flex flex-col gap-4">
                <div class="flex items-center gap-3">
                  <i class="pi pi-times-circle text-3xl text-red-500 shrink-0"></i>
                  <p class="text-lg font-medium text-gray-800 dark:text-neutral-200">
                    {{ errorLabel() }}
                  </p>
                </div>
                @if (errorMessage()) {
                  <pre
                    class="max-h-48 overflow-auto rounded-lg border p-3 text-xs whitespace-pre-wrap break-words
                           bg-gray-100 border-gray-200 text-gray-700
                           dark:bg-zinc-800 dark:border-zinc-600 dark:text-zinc-300">{{ errorMessage() }}</pre>
                }
                <div class="flex justify-end gap-2">
                  @if (errorMessage()) {
                    <p-button
                      [label]="copyLabel()"
                      [icon]="copyIcon()"
                      severity="secondary"
                      size="small"
                      (onClick)="onCopyError()" />
                  }
                  <p-button
                    label="Cerrar"
                    icon="pi pi-times"
                    severity="danger"
                    size="small"
                    (onClick)="dismiss.emit()" />
                </div>
              </div>
            }
          }
        </div>
      </div>
    }
  `,
})
export class ActionOverlay {
  state = input.required<OverlayState>();
  errorMessage = input<string | null>(null);
  loadingLabel = input<string>('Cargando...');
  loadingSublabel = input<string>('Esto puede tomar unos segundos');
  successLabel = input<string>('Operación completada exitosamente');
  errorLabel = input<string>('Error al completar la operación');
  dismiss = output<void>();

  private copied = signal(false);

  copyLabel = computed(() => (this.copied() ? 'Copiado' : 'Copiar error'));
  copyIcon = computed(() => (this.copied() ? 'pi pi-check' : 'pi pi-copy'));

  ariaLabel = computed(() => {
    switch (this.state()) {
      case 'loading':
        return this.loadingLabel();
      case 'success':
        return this.successLabel();
      case 'error':
        return this.errorLabel();
      default:
        return '';
    }
  });

  onCopyError() {
    const msg = this.errorMessage();
    if (msg) {
      navigator.clipboard.writeText(msg);
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    }
  }
}
